/**
 * Estado real de la conexión: ¿se llega al servidor desde este dispositivo?
 *
 * **Por qué no alcanza con `navigator.onLine`.** Ese flag sólo dice que la
 * interfaz de red está levantada. Con datos móviles activos y el servidor
 * inalcanzable devuelve `true` igual, así que la app se creía en línea y
 * mostraba la nube verde de "todo enviado" mientras no llegaba a ningún lado.
 *
 * No es un caso raro: es el caso normal en una obra al borde de la cobertura,
 * en una WiFi sin salida a internet, o detrás de un portal cautivo. Y es la
 * peor mentira que puede decir esta app — alguien que carga un parte a 80 km
 * necesita saber si eso llegó o sigue en el teléfono. Si la barra miente, deja
 * de confiar y vuelve a anotar en papel, que es justo lo que vinimos a evitar.
 *
 * Acá `navigator.onLine` se usa sólo como lo que sirve: un "no" definitivo. Si
 * dice que no hay red, no hay red y no hace falta gastar batería sondeando. Si
 * dice que sí, todavía no sabemos nada y hay que preguntarle al servidor.
 */

import { API_ROOT } from '../services/apiClient';

// ─── Estados ──────────────────────────────────────────────────────────────────

/** Todavía no se sondeó. La app arranca acá, y no miente en ninguna dirección. */
export const VERIFICANDO = 'verificando';
/** El servidor contestó. */
export const CON_SERVIDOR = 'con-servidor';
/** Hay red, pero el servidor no contesta. El caso traicionero. */
export const SIN_SERVIDOR = 'sin-servidor';
/** El dispositivo dice que no hay red. */
export const SIN_RED = 'sin-red';

// ─── Parámetros ───────────────────────────────────────────────────────────────

/**
 * Cuánto se espera a la sonda. Corto a propósito: la pregunta es "¿estás ahí?",
 * y un servidor que tarda más que esto en contestar un JSON de doce bytes, a
 * los fines de quien está parado al lado de una máquina, no está.
 */
const TIMEOUT_MS = 5000;

/** Cada cuánto se resondea con la app en primer plano. */
const INTERVALO_MS = 30000;

const URL_SONDA = `${API_ROOT}/taller/ping/`;

// ─── Estado del módulo ────────────────────────────────────────────────────────

let estado = VERIFICANDO;
const suscriptores = new Set();
let sondeoEnCurso = null;
let temporizador = null;
let vigilando = false;

function anunciar(nuevo) {
  if (nuevo === estado) return;
  estado = nuevo;
  for (const cb of suscriptores) {
    try {
      cb(estado);
    } catch {
      // Un suscriptor roto no puede tumbar a los demás ni al sondeo.
    }
  }
}

// ─── Lectura ──────────────────────────────────────────────────────────────────

export function estadoConexion() {
  return estado;
}

/**
 * ¿Conviene intentar la red ahora?
 *
 * `VERIFICANDO` cuenta como que sí: mientras no sepamos, el intento optimista es
 * lo correcto. Si falla, quien llama ya cae en la cola — que es el mismo camino
 * que habría tomado de haber sabido de antemano que no había servidor, sólo que
 * sin perder la oportunidad de que sí lo hubiera.
 */
export function hayServidor() {
  return estado === CON_SERVIDOR || estado === VERIFICANDO;
}

export function suscribirseAConexion(callback) {
  suscriptores.add(callback);
  return () => suscriptores.delete(callback);
}

// ─── Sondeo ───────────────────────────────────────────────────────────────────

async function sondear() {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return SIN_RED;
  }

  const control = new AbortController();
  const vencido = setTimeout(() => control.abort(), TIMEOUT_MS);

  try {
    // `fetch` y no el apiClient a propósito: el interceptor de axios reacciona
    // al 401 renovando el token y, si eso falla, borra la sesión y manda a
    // /login. Una sonda de alcance jamás debe poder desloguear a nadie.
    await fetch(URL_SONDA, {
      method: 'GET',
      signal: control.signal,
      cache: 'no-store',
      // Sin credenciales ni Authorization: la pregunta es si el servidor está,
      // no si la sesión sirve.
      credentials: 'omit',
    });
    // Cualquier respuesta HTTP —incluso 500 o 429— significa que el servidor
    // contestó, que es exactamente lo que se estaba preguntando. Sólo un error
    // de red o el timeout significan que no se llega.
    return CON_SERVIDOR;
  } catch {
    return SIN_SERVIDOR;
  } finally {
    clearTimeout(vencido);
  }
}

/**
 * Sondea ahora y devuelve el estado resultante.
 *
 * Varias llamadas simultáneas comparten un mismo sondeo: al volver la red se
 * disparan a la vez el evento `online`, el `visibilitychange` y el reintento de
 * la cola, y no tiene sentido pegarle tres veces al servidor para preguntarle
 * lo mismo.
 */
export function sondearAhora() {
  if (sondeoEnCurso) return sondeoEnCurso;

  sondeoEnCurso = sondear()
    .then((resultado) => {
      anunciar(resultado);
      return resultado;
    })
    .finally(() => {
      sondeoEnCurso = null;
    });

  return sondeoEnCurso;
}

/**
 * Actualiza el estado con lo que aprendió una petición real.
 *
 * Un envío de la cola que llega es la mejor prueba posible de que hay servidor,
 * y uno que falla por red es la mejor prueba de que no. Aprovecharlas evita
 * tener que esperar hasta el próximo sondeo para que la barra se entere de algo
 * que la app ya sabe.
 */
export function informarResultadoDeRed(llego) {
  if (llego) {
    anunciar(CON_SERVIDOR);
    return;
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    anunciar(SIN_RED);
    return;
  }
  anunciar(SIN_SERVIDOR);
}

// ─── Vigilancia ───────────────────────────────────────────────────────────────

/**
 * Arranca el seguimiento del estado de conexión. Idempotente.
 *
 * Se sondea al abrir, cuando el dispositivo avisa que volvió la red, cuando la
 * app vuelve al frente, y cada tanto mientras esté visible. Lo último es la
 * clave del caso traicionero: si el servidor se cae —o el camión sale del área
 * de cobertura— sin que cambie nada del lado del dispositivo, ningún evento se
 * dispara y sólo el sondeo periódico se entera.
 *
 * Con la app en segundo plano no se sondea: nadie está mirando la barra y el
 * teléfono de alguien que trabaja todo el día en el campo no tiene batería para
 * regalar.
 */
export function iniciarVigilanciaDeConexion() {
  if (vigilando) return () => {};
  vigilando = true;

  const detener = () => {
    if (temporizador) {
      clearInterval(temporizador);
      temporizador = null;
    }
  };

  const arrancar = () => {
    detener();
    temporizador = setInterval(sondearAhora, INTERVALO_MS);
  };

  const alVolverLaRed = () => sondearAhora();
  const alPerderLaRed = () => anunciar(SIN_RED);
  const alCambiarVisibilidad = () => {
    if (document.visibilityState === 'visible') {
      sondearAhora();
      arrancar();
    } else {
      detener();
    }
  };

  window.addEventListener('online', alVolverLaRed);
  window.addEventListener('offline', alPerderLaRed);
  document.addEventListener('visibilitychange', alCambiarVisibilidad);

  sondearAhora();
  arrancar();

  return () => {
    detener();
    window.removeEventListener('online', alVolverLaRed);
    window.removeEventListener('offline', alPerderLaRed);
    document.removeEventListener('visibilitychange', alCambiarVisibilidad);
    vigilando = false;
  };
}
